/**
 * OPR pricing on the clinic's patient panel (Treatment Plan spec v1.4, S-01 to
 * S-03), drawn from canvas boards 01 and 02: a price box on each drug card with
 * the faded OPR price beside a "Use" link, and the dark banner under the list.
 *
 * One price book, one query key. The treatment plan tab and the OPR Pricing tab
 * both read `['opr-items']` and write through `useOprPrices`, so a price typed
 * on either is on the other as soon as it is saved, and the banner and the
 * phone preview follow the box as it is typed.
 *
 * Costs, margins and OPR are clinic only (PG-006): nothing here is drawn on a
 * patient screen. The sums come from `pricingBanner` in @mcos/types, the same
 * function the API tests exercise, so the banner and the tests cannot drift.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SET_EVERY_PRICE, pricingBanner, type BannerLine } from '@mcos/types';
import { api } from '../lib/api';
import { IconCheck } from './icons';

export interface OprItem {
  itemId: string;
  treatmentId: string | null;
  therapyId: string | null;
  name: string;
  category: string;
  billingUnit: 'month' | 'visit' | 'bottle';
  unitLabel: string;
  needsPrescriberJustification: boolean;
  /** "2.5 mg weekly", "In-clinic infusion". */
  doseLine: string | null;
  /** What the patient reads under "How and how often". */
  howToTake: string;
  opr: {
    priceCents: number | null;
    label: 'Starting price' | 'OPR';
    confidence: 'low' | 'medium' | 'high' | 'n/a';
    basis: string;
    basisText: string;
    rangeLowCents: number | null;
    rangeHighCents: number | null;
  };
  public: {
    patientPriceCount: number;
    pharmacyPriceCount: number;
    patientPricesCents: number[];
    asOf: string | null;
  };
  learning: { plansSent: number; target: number };
  clinic: {
    priceCents: number | null;
    savedDefaultCents: number | null;
    /** The cost in use: the clinic's own, or the public reference. */
    costCents: number | null;
    /** The clinic's own cost, when it has typed one. */
    ownCostCents: number | null;
    costSource: string;
    /** S-03: "From ReviveRX price list, Nov 1" when a linked pharmacy's list set the cost. */
    costSourceLine?: string | null;
    estimated: boolean;
    /** Set while a price is being typed: what is saved, since `priceCents` then holds the draft. */
    committedPriceCents?: number | null;
  };
}

/** The price that is saved, whatever is being typed. */
export const savedPrice = (item: OprItem): number | null =>
  item.clinic.committedPriceCents !== undefined ? item.clinic.committedPriceCents : item.clinic.priceCents;

/** Whole dollars, as the boards draw margins: $106/mo, $69 est. */
export const wholeUsd = (cents: number): string => usd(Math.round(cents / 100) * 100);

export interface PriceChange {
  priceCents?: number | null;
  costCents?: number | null;
  saveAsDefault?: boolean;
}

export const OPR_KEY = ['opr-items'] as const;

export const usd = (cents: number | null | undefined): string =>
  cents == null
    ? 'none'
    : `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      })}`;

/** A box's content as dollars: 110 or 12.5, never "110.00". */
export const dollars = (cents: number | null | undefined): string => (cents == null ? '' : String(cents / 100));

export const toCents = (text: string): number | null => {
  const cleaned = text.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
};

/* ------------------------------------------------------------------ *
 * The data
 * ------------------------------------------------------------------ */

/**
 * The clinic's price book, shared by every screen that prices a plan. `items`
 * carry any price still being typed, so the banner and the preview move with
 * the box; a saved price is written into the cache straight away so nothing
 * flickers back while the request is in flight.
 */
export function useOprPrices() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: OPR_KEY,
    queryFn: () => api.get<{ items: OprItem[] }>('/opr/items'),
  });
  const [drafts, setDrafts] = useState<Record<string, number | null>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ itemId, change }: { itemId: string; change: PriceChange }) => api.put(`/opr/price-book/${itemId}`, change),
    onMutate: async ({ itemId, change }) => {
      setSaveError(null);
      await queryClient.cancelQueries({ queryKey: OPR_KEY });
      const previous = queryClient.getQueryData<{ items: OprItem[] }>(OPR_KEY);
      queryClient.setQueryData<{ items: OprItem[] }>(OPR_KEY, (current) =>
        current
          ? {
              items: current.items.map((item) =>
                item.itemId !== itemId
                  ? item
                  : {
                      ...item,
                      clinic: {
                        ...item.clinic,
                        priceCents: change.priceCents !== undefined ? change.priceCents : item.clinic.priceCents,
                        ownCostCents: change.costCents !== undefined ? change.costCents : item.clinic.ownCostCents,
                        costCents: change.costCents != null ? change.costCents : item.clinic.costCents,
                        savedDefaultCents:
                          change.saveAsDefault && change.priceCents != null ? change.priceCents : item.clinic.savedDefaultCents,
                      },
                    },
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (caught, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(OPR_KEY, context.previous);
      setSaveError(caught instanceof Error ? caught.message : 'Could not save the price');
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: OPR_KEY }),
  });

  const items = useMemo(
    () =>
      (query.data?.items ?? []).map((item) =>
        item.itemId in drafts
          ? { ...item, clinic: { ...item.clinic, committedPriceCents: item.clinic.priceCents, priceCents: drafts[item.itemId] ?? null } }
          : item,
      ),
    [query.data, drafts],
  );
  const byTreatment = useMemo(
    () => new Map(items.filter((item) => item.treatmentId).map((item) => [item.treatmentId as string, item])),
    [items],
  );
  const byItemId = useMemo(() => new Map(items.map((item) => [item.itemId, item])), [items]);

  return {
    items,
    byTreatment,
    byItemId,
    isLoading: query.isLoading,
    error: query.error,
    saveError,
    save: (itemId: string, change: PriceChange) => mutation.mutate({ itemId, change }),
    /** Show a price while it is being typed; `undefined` drops it once it is saved. */
    setDraft: (itemId: string, cents: number | null | undefined) =>
      setDrafts((current) => {
        const next = { ...current };
        if (cents === undefined) delete next[itemId];
        else next[itemId] = cents;
        return next;
      }),
    /** "Use OPR for all": every empty box takes its OPR price. */
    useOprForAll: (target: OprItem[]) =>
      Promise.all(
        target
          .filter((item) => item.clinic.priceCents == null && item.opr.priceCents != null)
          .map((item) => mutation.mutateAsync({ itemId: item.itemId, change: { priceCents: item.opr.priceCents } })),
      ),
  };
}

export function bannerLines(items: OprItem[]): BannerLine[] {
  return items.map((item) => ({
    unit: item.billingUnit,
    priceCents: item.clinic.priceCents,
    costCents: item.clinic.costCents,
    estimated: item.clinic.estimated,
  }));
}

/* ------------------------------------------------------------------ *
 * S-01: the price box on a drug card (board 01)
 * ------------------------------------------------------------------ */

/**
 * The `$` box, then the faded OPR price with its tag and a "Use" link. It is
 * a sibling of the card's tick button, never inside it: an input cannot sit in
 * a button. Typing sets the price on blur or Enter; Tab on an empty box, or
 * "Use", accepts the OPR price (PR-004).
 */
export function PriceBox({
  item,
  onSave,
  onDraft,
}: {
  item: OprItem;
  onSave: (change: PriceChange) => void;
  onDraft?: (cents: number | null | undefined) => void;
}) {
  const current = savedPrice(item);
  const oprCents = item.opr.priceCents;
  const [text, setText] = useState<string | null>(null);
  const set = item.clinic.priceCents != null;
  const differs = set && current !== oprCents && current !== item.clinic.savedDefaultCents;

  const commit = () => {
    if (text === null) return;
    const typed = text.trim() === '' ? null : toCents(text);
    setText(null);
    onDraft?.(undefined);
    if (typed !== current && (typed != null || current != null)) onSave({ priceCents: typed });
  };

  return (
    // Clicks in here never tick the card (the card's click handler skips this marker).
    <div className="flex w-[158px] shrink-0 flex-col gap-1" data-price-box="" data-testid={`price-box-${item.itemId}`}>
      <label
        className={`flex h-11 items-center gap-[3px] rounded-[9px] border bg-white pl-3 pr-2.5 ${
          set ? 'border-solid border-forest' : 'border-dashed border-[#b9b4a8]'
        }`}
      >
        <span className={`text-[16px] font-semibold ${set ? 'text-navy' : 'text-[#a3a8ad]'}`}>$</span>
        <input
          inputMode="decimal"
          aria-label={`Price for ${item.name}`}
          className="w-14 min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] font-semibold text-navy outline-none placeholder:text-[#a3a8ad]"
          placeholder={oprCents != null ? dollars(oprCents) : 'Price'}
          value={text ?? dollars(current)}
          onFocus={() => setText(dollars(current))}
          onChange={(event) => {
            setText(event.target.value);
            onDraft?.(event.target.value.trim() === '' ? null : toCents(event.target.value));
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            // Tab accepts the faded OPR price when the box is empty (PR-004).
            if (event.key === 'Tab' && !set && !(text ?? '').trim() && oprCents != null) {
              setText(null);
              onDraft?.(undefined);
              onSave({ priceCents: oprCents });
            }
          }}
        />
        <span className="text-[13px] text-[#5b6168]">{item.unitLabel}</span>
      </label>
      <div className="flex items-center gap-1.5 text-[12px]">
        <span className="rounded bg-[#e6f0ea] px-1.5 py-px font-semibold text-[#1b3f35]">OPR</span>
        <span className="text-[#5b6168]">{oprCents != null ? usd(oprCents) : 'No price yet'}</span>
        {oprCents != null && (
          <button
            type="button"
            className="ml-auto px-1 py-0.5 text-[12px] font-semibold text-forest underline"
            onClick={() => {
              setText(null);
              onDraft?.(undefined);
              onSave({ priceCents: oprCents });
            }}
          >
            Use
          </button>
        )}
      </div>
      {oprCents == null && !set && <span className="text-[11px] leading-snug text-[#5b6168]">Enter your cost on the OPR Pricing tab</span>}
      {differs && (
        <button
          type="button"
          className="self-start text-[11px] text-[#5b6168] underline"
          onClick={() => onSave({ priceCents: current, saveAsDefault: true })}
        >
          Save as my default
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * S-02: the dark banner (boards 01 and 02)
 * ------------------------------------------------------------------ */

/**
 * What the patient pays and what the clinic keeps, under the list. The status
 * line is the board's: it names what is left to do until every price is set,
 * then says the patient sees exactly these prices. `onOpenOpr` draws the link
 * to the OPR Pricing tab; the tab's own banner leaves it out.
 */
export function PricingBanner({
  items,
  unpriced = 0,
  firstName,
  onOpenOpr,
}: {
  items: OprItem[];
  /** Selected drugs that have no price book row at all; they count as unpriced. */
  unpriced?: number;
  firstName: string;
  onOpenOpr?: () => void;
}) {
  const banner = pricingBanner(bannerLines(items));
  const allSet = banner.allSet && unpriced === 0;
  const margin = banner.marginMonthlyCents;
  const visitMargin = banner.marginPerVisitCents;

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl bg-[#1c2b3a] px-[22px] py-[18px] text-white"
      data-testid="pricing-banner"
    >
      <div>
        <div className="text-[13px] text-[#c9d1d9]">Patient pays</div>
        <div className="font-display text-[26px] font-semibold leading-tight" data-testid="pricing-monthly">
          {usd(banner.monthlyCents)}/mo
        </div>
        {banner.perVisitCents > 0 && (
          <div className="text-[13px] text-[#c9d1d9]" data-testid="pricing-visit">
            + {usd(banner.perVisitCents)} per in-clinic visit
          </div>
        )}
      </div>
      <div>
        <div className="text-[13px] text-[#c9d1d9]">{banner.marginEstimated ? 'Your margin (est.)' : 'Your margin'}</div>
        <div className="font-display text-[26px] font-semibold leading-tight" data-testid="pricing-margin">
          {margin != null ? `${wholeUsd(margin)}/mo` : visitMargin != null ? `${wholeUsd(visitMargin)}/visit` : 'No cost yet'}
        </div>
        {margin != null && visitMargin != null && (
          <div className="text-[13px] text-[#c9d1d9]">+ {wholeUsd(visitMargin)} per in-clinic visit</div>
        )}
      </div>
      <div className="flex min-w-[220px] flex-1 items-center gap-2.5 text-[15px]">
        {allSet && (
          <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-[#2e7d5b]">
            <IconCheck size={15} />
          </span>
        )}
        <span className="text-[#e7ecf0]" data-testid="pricing-status">
          {allSet ? banner.status(firstName) : SET_EVERY_PRICE}
        </span>
      </div>
      {onOpenOpr && (
        <button
          type="button"
          onClick={onOpenOpr}
          className="flex min-h-11 shrink-0 items-center rounded-[9px] border border-[#52606e] px-4 text-[15px] font-semibold text-white hover:bg-white/10"
        >
          Open OPR Pricing
        </button>
      )}
    </div>
  );
}
